import { useCallback, useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, InputBase, Stack } from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridRowId,
    GridRowSelectionModel,
} from '@mui/x-data-grid';
import { ProductCatalogue, ProductType } from '@prisma/client';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { RadioGroupField } from 'src/components/common/form/FormRadioGroup';
import TextEditor from 'src/components/common/TextEditor';
import { FileUploadDropDown, NPIAutoComplete } from 'src/components/FormikComponents';
import { AutoCompleteOptionType } from 'src/components/FormikComponents/NPIAutoComplete';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { CREATE_PRODUCT_MUTATION } from 'src/graphql/mutations/products.mutation';
import { FIND_PRIMARY_PRODUCT_MUTATION, GET_PRODUCTS } from 'src/graphql/queries/product.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { countryConfig } from 'src/lib/countryConfig';

interface CreateProductInput {
    name: string;
    catalogue: ProductCatalogue;
    type: string;
    description: { type: string; text: string }[] | string;
    countries: string[];
    imageUrl?: string;
    primaryProductIds?: number[];
    productFile?: {
        locationUrl: string;
    };
}

export const DELETE_UPLOAD_URL = gql`
    mutation deleteUploadURL($url: String!) {
        deleteUploadURL(url: $url) {
            message
        }
    }
`;

const AddProductPage = () => {
    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Product ID', width: 150 },
        { field: 'name', headerName: 'Product Name', width: 350 },
        { field: 'type', headerName: 'Product Type', width: 350 },
    ];

    const productDocTypes = [
        {
            key: 'Product',
            value: 'Product',
        },
    ];

    const timeout = useRef(null);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 5,
        page: 0,
    });
    const mapPageToNextCursor = React.useRef<{ [page: number]: GridRowId }>({});
    const [erorrMessage, setErrorMessage] = useState('');
    const [primaryProduct, setPrimaryProduct] = useState('');
    const [primaryProductList, setPrimaryProductList] = useState([]);
    const [selectedPrimaryProductIds, setSelectedPrimaryProductIds] = useState<number[]>([]);
    const [selectedPrimaryProducts, setSelectedPrimaryProducts] = useState([]);
    const [uploadUrl, setUploadUrl] = useState('');
    const [removeUploadImage] = useState(false);
    const [selectedCatalogue, setSelectedCatalogue] = useState<ProductCatalogue>(
        ProductCatalogue.PRIMARY
    );
    const [selectedType, setSelectedType] = useState('');
    const [uploadErrorMessage, setUploadErrorMessage] = useState('');
    const [descriptionTextValues, setDescriptionTextValues] = useState();
    const [descriptionErrorMessage, setDescriptionErrorMessage] = useState('');
    const [rowCountState, setRowCountState] = useState(0);
    const [countries] = useState([
        {
            id: 1,
            label: 'ALL',
            value: 'ALL',
        },
        {
            id: 2,
            label: countryConfig.IE.name,
            value: countryConfig.IE.name,
        },
        {
            id: 3,
            label: countryConfig.GB.name,
            value: countryConfig.GB.name,
        },
        {
            id: 4,
            label: countryConfig.XI.name,
            value: countryConfig.XI.name,
        },
    ]);

    const [findPrimaryProduct, { loading: primaryProductLoading }] = useLazyQuery(
        FIND_PRIMARY_PRODUCT_MUTATION
    );

    const [getProducts, { loading: getProductsLoading }] = useLazyQuery(GET_PRODUCTS);

    // Get primary products of selected type (online / terminal) if no search term
    const getProductData = useCallback(
        async ({ input, page }) => {
            const products = await getProducts({ variables: { page, input } });

            if (products) {
                const {
                    total = 0,
                    page = 0,
                    pageSize = 0,
                    data = [],
                } = products?.data?.products ?? {};

                const pageProductsAndSelectedProducts = [
                    ...products?.data?.products?.data?.filter(({ id }) => {
                        return !selectedPrimaryProductIds?.includes(id);
                    }),
                    ...selectedPrimaryProducts,
                ];

                setPrimaryProductList(pageProductsAndSelectedProducts);

                setRowCountState(total);

                setPaginationModel({ page, pageSize });
            }
        },
        [getProducts, selectedPrimaryProductIds, selectedPrimaryProducts]
    );

    // Get primary products of selected type (online / terminal) when search term entered
    const getPrimaryProduct = useCallback(
        async ({ name, type, page }) => {
            const products = await findPrimaryProduct({
                variables: {
                    name,
                    type,
                    page,
                },
            });

            if (products) {
                const {
                    total = 0,
                    page = 0,
                    pageSize = 0,
                    data = [],
                } = products?.data?.findPrimaryProduct ?? {};

                const pageProductsAndSelectedProducts = [
                    ...data.filter(({ id }) => {
                        return !selectedPrimaryProductIds?.includes(id);
                    }),
                    ...selectedPrimaryProducts,
                ];

                setPrimaryProductList(pageProductsAndSelectedProducts);

                setRowCountState(total);
                setPaginationModel({ page, pageSize });
                if (!data?.length) {
                    showErrorToast('No record found');
                }
            }
        },
        [findPrimaryProduct, selectedPrimaryProductIds, selectedPrimaryProducts]
    );

    const handleFindPrimaryProduct = useCallback(
        async ({ page }) => {
            // If searching for a primary product
            if (primaryProduct) {
                await getPrimaryProduct({
                    name: primaryProduct,
                    type: selectedType,
                    page,
                });
            } else {
                // If no search term entered get all of selected type
                await getProductData({
                    input: { type: selectedType, catalogue: ProductCatalogue.PRIMARY },
                    page,
                });
            }
        },
        [getPrimaryProduct, getProductData, primaryProduct, selectedType]
    );

    useEffect(() => {
        // Fetch primary products if secondary and product type is selected or type changed but no search term
        if (!selectedType) return;

        const selectedTypeChanged = primaryProductList?.[0]?.type !== selectedType;
        if (
            (selectedCatalogue === ProductCatalogue.SECONDARY &&
                selectedType &&
                (!primaryProductList || primaryProductList?.length < 1)) ||
            (selectedTypeChanged && primaryProduct === '')
        ) {
            getProductData({
                input: { type: selectedType, catalogue: ProductCatalogue.PRIMARY },
                page: paginationModel,
            });
        } else if (selectedTypeChanged && primaryProduct !== '') {
            // Type changed but has a search term
            getPrimaryProduct({
                name: primaryProduct,
                type: selectedType,
                page: paginationModel,
            });
        }
    }, [
        selectedCatalogue,
        selectedType,
        getProductData,
        getPrimaryProduct,
        paginationModel,
        primaryProductList,
        primaryProduct,
    ]);

    const handlePaginationModelChange = async (newPaginationModel: GridPaginationModel) => {
        if (
            newPaginationModel.page === 0 ||
            mapPageToNextCursor.current[newPaginationModel.page - 1]
        ) {
            setPaginationModel(newPaginationModel);
        }

        await handleFindPrimaryProduct({ page: newPaginationModel });
    };

    const handleSelectedPrimaryProductsIds = (ids: number[]) => {
        setSelectedPrimaryProductIds(ids);
    };

    const [createProduct, { loading: loadingCreateProduct }] = useMutation(
        CREATE_PRODUCT_MUTATION,
        {
            onCompleted: ({ createProduct }) => {
                showSuccessToast(`${createProduct.name} product created`);
                timeout.current = setTimeout(() => {
                    return navigate('/products');
                }, 1000);
            },
            refetchQueries: [
                {
                    query: GET_PRODUCTS,
                    variables: { input: {}, page: { page: 0, pageSize: 5 } },
                },
            ],
            onError: (error) => {
                showErrorToast(error.message);
            },
        }
    );

    const [deleteUploadURL] = useMutation(DELETE_UPLOAD_URL, {
        onCompleted: () => {
            showSuccessToast('Upload remove successfully');
            setUploadUrl('');
        },
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    const onSubmit = async (input: CreateProductInput, { resetForm }) => {
        setErrorMessage('');
        setDescriptionErrorMessage('');

        if (!primaryProductList.length && input.catalogue === ProductCatalogue.SECONDARY) {
            setErrorMessage('Primary product is required for secondary product catalogue');
            return false;
        }

        const data =
            input.catalogue === ProductCatalogue.SECONDARY
                ? { ...input, primaryProductIds: selectedPrimaryProductIds }
                : { ...input };

        data.imageUrl = input.productFile?.locationUrl ?? '';
        delete data.productFile;

        if (!data.imageUrl) {
            setUploadErrorMessage('Product image is required');
            return;
        }

        data.description = descriptionTextValues;
        if (!data.description) {
            setDescriptionErrorMessage('Description is required');
            return;
        }

        data.countries = (input.countries as unknown as AutoCompleteOptionType[]).map((country) => {
            return country.value as string;
        });

        await createProduct({ variables: { input: data } });
        resetForm();
        setSelectedType('');
    };

    const removeUploadUrl = async () => {
        await deleteUploadURL({
            variables: {
                url: uploadUrl.toString(),
            },
        });

        setUploadUrl('');
        setUploadErrorMessage('');
    };

    useEffect(() => {
        return () => {
            return clearTimeout(timeout.current);
        };
    }, [timeout]);

    const initialValues = {
        name: '',
        catalogue: ProductCatalogue.PRIMARY,
        type: '',
        description: '',
        imageUrl: '',
        countries: [],
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        catalogue: Yup.string().required('Catalogue is required'),
        type: Yup.string().required('Type is required'),
        description: Yup.string().optional(),
        imageUrl: Yup.string().optional(),
        countries: Yup.array()
            .of(Yup.object())
            .required('Country is required')
            .min(1, 'Country is required'),
    });

    return (
        <PageContent heading="Add Product Catalogue">
            <MetaTags title="Add Product" description="Add Product" />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(props: FormikProps<CreateProductInput>) => {
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard>
                                    <h2 className="font-normal mb-0 mt-10">Product Information</h2>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <InputBaseField
                                                            label="Product Name"
                                                            name="name"
                                                        />
                                                        <Box>
                                                            <RadioGroupField
                                                                label="Product Catalogue"
                                                                containerGridSpacing={3}
                                                                labelGridSize={3}
                                                                radioGroupGridSize={6}
                                                                radioOptionGridSize={6}
                                                                name="catalogue"
                                                                setselectedvalue={
                                                                    setSelectedCatalogue
                                                                }
                                                                options={[
                                                                    {
                                                                        label: 'Primary',
                                                                        value: ProductCatalogue.PRIMARY,
                                                                    },
                                                                    {
                                                                        label: 'Secondary',
                                                                        value: ProductCatalogue.SECONDARY,
                                                                    },
                                                                ]}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <RadioGroupField
                                                                label="Product Type"
                                                                name="type"
                                                                radioOptionGridSize={6}
                                                                setselectedvalue={setSelectedType}
                                                                options={[
                                                                    {
                                                                        label: 'Online',
                                                                        value: ProductType.ONLINE,
                                                                    },
                                                                    {
                                                                        label: 'Terminal',
                                                                        value: ProductType.TERMINAL,
                                                                    },
                                                                ]}
                                                            />
                                                        </Box>
                                                        <Box marginTop={1}>
                                                            <NPIAutoComplete
                                                                header="Country"
                                                                placeholder="Select Country"
                                                                name="countries"
                                                                multiple
                                                                required
                                                                options={countries}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Box>
                                                            <TextEditor
                                                                label="Product Description"
                                                                setTextValues={
                                                                    setDescriptionTextValues
                                                                }
                                                                errorMessage={
                                                                    descriptionErrorMessage
                                                                }
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                {uploadUrl && !removeUploadImage ? (
                                                    <Box>
                                                        <img
                                                            src={`${uploadUrl}`}
                                                            alt="upload"
                                                            width="120px"
                                                        />
                                                        <Button
                                                            sx={{ margin: '-10px 0 0 20px' }}
                                                            onClick={removeUploadUrl}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <FileUploadDropDown
                                                            name="productFile"
                                                            fileTypes={productDocTypes.map(
                                                                ({ key, value }) => {
                                                                    return {
                                                                        value,
                                                                        label: key,
                                                                    };
                                                                }
                                                            )}
                                                            label="Product Image"
                                                        />
                                                        <label className="error">
                                                            {uploadErrorMessage}
                                                        </label>
                                                    </>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                </MainCard>

                                {selectedCatalogue === ProductCatalogue.SECONDARY &&
                                    selectedType !== '' && (
                                        <MainCard>
                                            <h2 className="font-normal mb-0 mt-10">
                                                Product Information
                                            </h2>
                                            <Box>
                                                <Stack>
                                                    <label className="font-bold mt-30">
                                                        Link to Primary Product
                                                    </label>
                                                    <Box className="mt-10">
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={12} md={6}>
                                                                <Grid container>
                                                                    <Grid
                                                                        item
                                                                        xs={9}
                                                                        className="flex-col"
                                                                    >
                                                                        <InputBase
                                                                            name="primaryProduct"
                                                                            className={`form-input-group ${
                                                                                erorrMessage
                                                                                    ? 'input-error'
                                                                                    : ''
                                                                            }`}
                                                                            onChange={(event) => {
                                                                                return setPrimaryProduct(
                                                                                    event.target
                                                                                        .value
                                                                                );
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid
                                                                        item
                                                                        xs={3}
                                                                        className="flex-col"
                                                                    >
                                                                        <Button
                                                                            variant="outlined"
                                                                            onClick={() => {
                                                                                return handleFindPrimaryProduct(
                                                                                    {
                                                                                        page: paginationModel,
                                                                                    }
                                                                                );
                                                                            }}
                                                                            className={`btn-input-group btn-primary ${
                                                                                erorrMessage
                                                                                    ? 'btn-error'
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            Search
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                    {erorrMessage && (
                                                        <label className="error">
                                                            Primary product ID is required
                                                        </label>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            marginTop: '20px',
                                                            border: 'thin solid #FDC800',
                                                            borderRadius: '10px',
                                                        }}
                                                    >
                                                        <DataGrid
                                                            autoHeight
                                                            rows={primaryProductList}
                                                            columns={columns}
                                                            disableColumnMenu
                                                            disableColumnFilter
                                                            disableColumnSelector
                                                            paginationMode="server"
                                                            getRowHeight={() => {
                                                                return 'auto';
                                                            }}
                                                            paginationModel={paginationModel}
                                                            checkboxSelection
                                                            onPaginationModelChange={
                                                                handlePaginationModelChange
                                                            }
                                                            pageSizeOptions={[5, 10, 25]}
                                                            rowCount={rowCountState}
                                                            sx={{
                                                                '.MuiDataGrid-columnHeaders': {
                                                                    backgroundColor: '#FFFAE5',
                                                                    color: '#000',
                                                                    borderRadius: '10px 10px 0 0',
                                                                },
                                                            }}
                                                            keepNonExistentRowsSelected
                                                            onRowSelectionModelChange={(
                                                                rowSelectionModel: GridRowSelectionModel
                                                            ) => {
                                                                handleSelectedPrimaryProductsIds(
                                                                    rowSelectionModel
                                                                );
                                                                setSelectedPrimaryProducts(
                                                                    (prevState) => {
                                                                        return [
                                                                            ...prevState.filter(
                                                                                (item) => {
                                                                                    return rowSelectionModel.includes(
                                                                                        item.id
                                                                                    );
                                                                                }
                                                                            ),
                                                                            ...primaryProductList.filter(
                                                                                (item) => {
                                                                                    return (
                                                                                        rowSelectionModel.includes(
                                                                                            item.id
                                                                                        ) &&
                                                                                        !prevState.find(
                                                                                            (
                                                                                                prevItem
                                                                                            ) => {
                                                                                                return (
                                                                                                    prevItem.id ===
                                                                                                    item.id
                                                                                                );
                                                                                            }
                                                                                        )
                                                                                    );
                                                                                }
                                                                            ),
                                                                        ];
                                                                    }
                                                                );
                                                            }}
                                                            loading={
                                                                primaryProductLoading ||
                                                                getProductsLoading
                                                            }
                                                        />
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        </MainCard>
                                    )}
                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/products');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loadingCreateProduct ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Add Product
                                            </LoadingButton>
                                        ) : (
                                            <>
                                                <Box className="btn-wrapper">
                                                    <Button
                                                        className={`${
                                                            props.dirty && props.isValid
                                                                ? 'btn-primary'
                                                                : 'btn-dark'
                                                        } btn-medium`}
                                                        type="submit"
                                                        variant="contained"
                                                        size="medium"
                                                        disableElevation
                                                        onClick={(_event) => {
                                                            if (
                                                                !props.values.name ||
                                                                !props.values.catalogue ||
                                                                !props.values.type
                                                            ) {
                                                                showErrorToast(
                                                                    'Please fill in required fields'
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        Add Product
                                                    </Button>
                                                </Box>
                                            </>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default AddProductPage;
